import { BaseComponent, BElement } from "utils/base-component"

/** Same as `--small-up` in postcss.config.js (576px). */
const SMALL_UP_MEDIA_QUERY = "(min-width: 36rem)"
const SHORT_VIEWPORT_MEDIA_QUERY = "(max-height: 1080px)"

function isBelowSmallUpViewport(): boolean {
  return !window.matchMedia(SMALL_UP_MEDIA_QUERY).matches
}

export class LocationSubheader extends BaseComponent {
  static cssClass = "location-subheader"

  private navButtons!: BElement[]
  private subNavContainers!: BElement[]
  private activeTabId: string | null = null
  private tabIndicator!: BElement | null
  private navList!: BElement | null
  private stickyNavWrapper!: HTMLElement | null
  private stickyNavContainer!: HTMLElement | null
  private stickyNavButton!: HTMLElement | null
  private isNavHiddenByToggle = false
  private isNavHiddenByObserver = false

  init() {
    const tabIndicatorEl = document.querySelector(`.${this.elementClassName("tab-indicator")}`)
    this.tabIndicator = tabIndicatorEl
      ? new BElement(tabIndicatorEl, this.elementClassName("tab-indicator"))
      : null

    const navListEl = document.querySelector(`.${this.elementClassName("nav-list")}`)
    this.navList = navListEl ? new BElement(navListEl, this.elementClassName("nav-list")) : null

    this.stickyNavWrapper = document.querySelector(
      `.${this.elementClassName("sticky-nav-wrapper")}`,
    ) as HTMLElement | null

    this.stickyNavContainer = document.querySelector(
      `.${this.elementClassName("sticky-nav-container")}`,
    ) as HTMLElement | null

    this.stickyNavButton = document.getElementById("sticky-nav-button")

    this.navButtons = Array.from(
      document.querySelectorAll(`.${this.elementClassName("nav-button")}`),
    ).map((el) => new BElement(el, this.elementClassName("nav-button")))

    this.subNavContainers = Array.from(
      document.querySelectorAll(`.${this.elementClassName("sub-nav-container")}`),
    ).map((el) => new BElement(el, this.elementClassName("sub-nav-container")))

    // Safety check: ensure we have buttons and containers
    if (this.navButtons.length === 0 || this.subNavContainers.length === 0) {
      return
    }

    // Detect current page and set initial active tab
    this.setActiveTabFromUrl()

    // Set active state on sub-nav-links based on current URL
    this.setActiveSubNavLink()

    // Position the tab indicator
    this.updateTabIndicator()

    // Set up click handlers for nav buttons
    this.navButtons.forEach((button) => {
      button.element.addEventListener("click", () => {
        const tabId = button.element.id
        if (tabId) {
          this.switchTab(tabId)
        }
      })
    })

    // Update indicator on nav-list scroll and window resize
    this.navList?.element.addEventListener("scroll", () =>
      requestAnimationFrame(() => this.updateTabIndicator()),
    )
    window.addEventListener("resize", () => requestAnimationFrame(() => this.updateTabIndicator()))

    // Sticky nav toggle: show/hide button when nav is in sticky position, and allow user to hide the bar
    if (this.stickyNavWrapper && this.stickyNavContainer && this.stickyNavButton) {
      type StickyNavState = {
        wasStickyActive: boolean
        hasManualToggleOverride: boolean
        prevBelowSmallUp: boolean
      }

      let ticking = false
      const stickyNavState: StickyNavState = {
        wasStickyActive: false,
        hasManualToggleOverride: false,
        prevBelowSmallUp: isBelowSmallUpViewport(),
      }
      const containerHiddenClass = `${this.elementClassName("sticky-nav-container")}--hidden`
      const buttonActiveClass = `${this.elementClassName("sticky-nav-button")}--active`
      const buttonNavHiddenClass = `${this.elementClassName("sticky-nav-button")}--nav-hidden`
      // Sidebar affiliate badges use a different sticky `top` depending on the sticky bar state
      const stickyNavShownClass = "sticky-nav-shown"
      const stickyNavHiddenClass = "sticky-nav-hidden"

      const syncAffiliateBadgesStickyTop = (isStickyActive: boolean, isNavHidden: boolean) => {
        const { classList } = document.documentElement
        classList.toggle(stickyNavShownClass, isStickyActive && !isNavHidden)
        classList.toggle(stickyNavHiddenClass, isStickyActive && isNavHidden)
      }

      const getCurrentNavHiddenState = () =>
        stickyNavState.hasManualToggleOverride
          ? this.isNavHiddenByToggle
          : this.isNavHiddenByToggle || this.isNavHiddenByObserver

      const resetToggleState = () => {
        this.isNavHiddenByToggle = false
        stickyNavState.hasManualToggleOverride = false
      }

      const applyStickyNavActiveState = (isStickyActive: boolean) => {
        this.stickyNavWrapper!.classList.toggle("active", isStickyActive)
        this.stickyNavButton!.classList.toggle(buttonActiveClass, isStickyActive)
      }

      const applyDefaultHiddenStateForViewport = (belowSmallUp: boolean) => {
        this.isNavHiddenByToggle = belowSmallUp
        stickyNavState.hasManualToggleOverride = false
      }

      const applyNavVisibility = (isStickyActive: boolean) => {
        const isNavHidden = getCurrentNavHiddenState()
        this.stickyNavButton!.classList.toggle(buttonNavHiddenClass, isNavHidden)
        this.stickyNavContainer!.classList.toggle(containerHiddenClass, isNavHidden)
        syncAffiliateBadgesStickyTop(isStickyActive, isNavHidden)
      }

      const enterStickyState = (belowSmallUp: boolean) => {
        const hasCrossedViewportBreakpoint = belowSmallUp !== stickyNavState.prevBelowSmallUp
        if (!stickyNavState.wasStickyActive || hasCrossedViewportBreakpoint) {
          applyDefaultHiddenStateForViewport(belowSmallUp)
        }
        stickyNavState.wasStickyActive = true
        applyStickyNavActiveState(true)
        applyNavVisibility(true)
      }

      const exitStickyState = () => {
        stickyNavState.wasStickyActive = false
        applyStickyNavActiveState(false)
        this.stickyNavButton!.classList.remove(buttonNavHiddenClass)
        this.stickyNavContainer!.classList.remove(containerHiddenClass)
        resetToggleState()
        syncAffiliateBadgesStickyTop(false, false)
      }

      const updateStickyState = () => {
        const { top } = this.stickyNavWrapper!.getBoundingClientRect()
        // Nav is in sticky position when its top edge has reached the viewport (top <= 8px)
        const isStickyActive = top <= 8
        const belowSmallUp = isBelowSmallUpViewport()

        if (isStickyActive) {
          enterStickyState(belowSmallUp)
        } else {
          exitStickyState()
        }

        stickyNavState.prevBelowSmallUp = belowSmallUp
        ticking = false
      }

      const onScroll = () => {
        if (!ticking) {
          ticking = true
          window.requestAnimationFrame(updateStickyState)
        }
      }

      this.stickyNavButton.addEventListener("click", () => {
        if (!this.stickyNavButton!.classList.contains(buttonActiveClass)) {
          return
        }
        const wasNavHidden = getCurrentNavHiddenState()
        stickyNavState.hasManualToggleOverride = true
        this.isNavHiddenByToggle = !wasNavHidden
        applyNavVisibility(true)
        // rAF: class/transform on the sticky container updates layout; sync after that frame.
        requestAnimationFrame(() => {
          applyNavVisibility(true)
        })
      })

      const forecastTableContent = document.querySelector(".forecast-table__content")
      if (forecastTableContent) {
        const shortViewportQuery = window.matchMedia(SHORT_VIEWPORT_MEDIA_QUERY)
        const syncObserverViewportState = () => {
          if (!shortViewportQuery.matches) {
            this.isNavHiddenByObserver = false
            if (this.stickyNavWrapper!.classList.contains("active")) {
              applyNavVisibility(true)
            }
          }
        }

        // Hide when the table top enters the upper ~90% of the viewport.
        const observerOptions = {
          threshold: 0,
          rootMargin: "0px 0px -10% 0px",
        }
        // eslint-disable-next-line compat/compat
        const observer = new IntersectionObserver(([entry]) => {
          if (stickyNavState.hasManualToggleOverride) {
            return
          }
          if (!shortViewportQuery.matches) {
            this.isNavHiddenByObserver = false
          } else {
            this.isNavHiddenByObserver = entry.isIntersecting
          }
          if (this.stickyNavWrapper!.classList.contains("active")) {
            applyNavVisibility(true)
          }
        }, observerOptions)

        shortViewportQuery.addEventListener("change", syncObserverViewportState)
        syncObserverViewportState()
        observer.observe(forecastTableContent)
      }

      updateStickyState()
      window.addEventListener("scroll", onScroll, { passive: true })
      window.addEventListener("resize", onScroll)
    }
  }

  /**
   * Detects the current URL and determines which tab should be active
   */
  private setActiveTabFromUrl() {
    const currentPath = window.location.pathname
    const tabId = LocationSubheader.detectTabFromUrl(currentPath)

    if (tabId) {
      this.switchTab(tabId)
    }
  }

  /**
   * Maps URL paths to tab IDs based on route patterns
   */
  private static detectTabFromUrl(path: string): string | null {
    // Live conditions: metar, snow-report, webcams
    // Check for specific live condition routes
    if (/\/resorts\/[^/]+\/(metar|snow-report|webcams|snowfinder)/.test(path)) {
      return "live"
    }

    // Forecast tabs: /resorts/:location/:tab (2day, 6day, 1hr, 16-day)
    // Also check for elevation sub-tabs (mid, top, bot) which are forecast pages
    if (
      /\/resorts\/[^/]+\/(2day|6day|1hr|16-day)/.test(path) ||
      /\/resorts\/[^/]+\/(mid|top|bot)/.test(path) ||
      /\/maps\/dynamic\/[^/]+/.test(path)
    ) {
      return "forecast"
    }

    // Resort info: /resorts/:location (base URL), reviews, photos, pistemap
    if (
      /\/resorts\/[^/]+$/.test(path) ||
      /\/resorts\/[^/]+\/(reviews|photos|pistemap|ski-hire)/.test(path)
    ) {
      return "resort-info"
    }

    // Snow history: /resorts/:location/history or /snowmail_subscriptions
    if (/\/resorts\/[^/]+\/history/.test(path) || /^\/snowmail_subscriptions\/?$/.test(path)) {
      return "snow-history"
    }

    // Default to forecast if no match
    return "forecast"
  }

  /**
   * Switches to a specific tab by ID
   * @param tabId - The ID of the tab to activate
   */
  private switchTab(tabId: string) {
    // Remove active class from all buttons and containers
    this.navButtons.forEach((button) => {
      button.element.classList.toggle("active", button.element.id === tabId)
    })

    this.subNavContainers.forEach((container) => {
      const isActive = container.element.id === tabId
      container.element.classList.toggle("active", isActive)

      // Also toggle active on the sub-nav-content inside
      const content = container.element.querySelector(
        `.${this.elementClassName("sub-nav-content")}`,
      )
      if (content) {
        content.classList.toggle("active", isActive)
      }
    })

    this.activeTabId = tabId

    // Update active sub-nav-link after switching tabs
    this.setActiveSubNavLink()

    // Update tab indicator position
    this.updateTabIndicator()
  }

  /**
   * Updates the tab indicator position to match the active nav-button
   */
  private updateTabIndicator() {
    if (!this.tabIndicator) return

    const activeButton = this.navButtons.find((b) => b.element.classList.contains("active"))
    if (!activeButton) {
      this.tabIndicator.element.style.width = "0"
      return
    }

    const navItem = activeButton.element.closest(`.${this.elementClassName("nav-item")}`)
    const locationTabs = this.tabIndicator.element.parentElement
    if (!navItem || !locationTabs) return

    const tabsRect = locationTabs.getBoundingClientRect()
    const itemRect = (navItem as HTMLElement).getBoundingClientRect()

    this.tabIndicator.element.style.left = `${itemRect.left - tabsRect.left - 16}px`
    this.tabIndicator.element.style.width = `${itemRect.width + 32}px`
  }

  /**
   * Sets the active class on sub-nav-link elements that match the current page URL
   */
  private setActiveSubNavLink() {
    // eslint-disable-next-line compat/compat
    const currentUrl = new URL(window.location.href)
    const currentPathname = currentUrl.pathname

    const subNavLinks = document.querySelectorAll<HTMLElement>(
      `.${this.elementClassName("sub-nav-link")}`,
    )

    subNavLinks.forEach((link) => {
      const href = link.getAttribute("href")
      if (!href) return

      // Normalize the href - handle relative and absolute URLs
      let linkPath: string
      try {
        // If href is absolute, use it directly
        if (href.startsWith("http://") || href.startsWith("https://")) {
          // eslint-disable-next-line compat/compat
          const linkUrl = new URL(href)
          linkPath = linkUrl.pathname
        } else {
          // For relative URLs, resolve against current location
          // eslint-disable-next-line compat/compat
          const linkUrl = new URL(href, window.location.origin)
          linkPath = linkUrl.pathname
        }

        // Special handling for forecast pages (6day vs 16-day)
        const currentForecast = LocationSubheader.parseForecastPath(currentPathname)
        const linkForecast = LocationSubheader.parseForecastPath(linkPath)

        let isActive = false

        if (currentForecast && linkForecast) {
          // Both are forecast pages - compare location and elevation
          isActive =
            currentForecast.location === linkForecast.location &&
            currentForecast.elevation === linkForecast.elevation
        } else {
          // Standard path comparison (case-insensitive, ignoring trailing slashes)
          const normalizedCurrent = currentPathname.replace(/\/$/, "").toLowerCase()
          const normalizedLink = linkPath.replace(/\/$/, "").toLowerCase()
          isActive = normalizedCurrent === normalizedLink
        }

        link.classList.toggle("active", isActive)
      } catch (e) {
        // If URL parsing fails, skip this link
        console.warn("Failed to parse link URL:", href, e) // eslint-disable-line no-console
      }
    })
  }

  /**
   * Parses a forecast page path to extract location and elevation
   * Handles both 6day and 16-day variants
   * @param path - The pathname to parse
   * @returns Object with location and elevation, or null if not a forecast path
   */
  private static parseForecastPath(path: string): { location: string; elevation: string } | null {
    // Match patterns like:
    // /resorts/Verbier/6day/mid
    // /resorts/Verbier/16-day/mid
    // /resorts/Verbier/6day/top
    const forecastMatch = path.match(/^\/resorts\/([^/]+)\/(6day|16-day)\/(mid|top|bot)$/i)

    if (forecastMatch) {
      return {
        location: forecastMatch[1].toLowerCase(),
        elevation: forecastMatch[3].toLowerCase(),
      }
    }

    return null
  }
}
